import { useState, useEffect } from 'react'
import FormModal from '../FormModal'

const formStyles = {
  field: {
    marginBottom: '0.4rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: 'clamp(160px, 26vh, 480px)',
    resize: 'vertical',
  },
  hint: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginTop: '0.25rem',
  },
}

function CharacterForm({ isOpen, onClose, character = null, onSubmit }) {
  const [formData, setFormData] = useState({
    character_name: '',
    appearance: '',
    personality: '',
    backstory: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [portraitFile, setPortraitFile] = useState(null)
  const [portraitPreview, setPortraitPreview] = useState(null)
  const [deletePortrait, setDeletePortrait] = useState(false)

  // Update form data when character prop changes
  useEffect(() => {
    if (character) {
      setFormData({
        character_name: character.character_name || '',
        appearance: character.appearance || '',
        personality: character.personality || '',
        backstory: character.backstory || '',
      })
      // Set existing portrait preview if available
      setPortraitPreview(character.appearance_image_url || null)
      setPortraitFile(null)
      setDeletePortrait(false)
    } else {
      setFormData({
        character_name: '',
        appearance: '',
        personality: '',
        backstory: '',
      })
      setPortraitPreview(null)
      setPortraitFile(null)
      setDeletePortrait(false)
    }
  }, [character, isOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePortraitChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setPortraitFile(file)
      setDeletePortrait(false) // Clear delete flag when uploading new file
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setPortraitPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemovePortrait = () => {
    setPortraitFile(null)
    setPortraitPreview(null)
    setDeletePortrait(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Pass form data, portrait file, and delete flag to parent
      await onSubmit(formData, portraitFile, deletePortrait)
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Edit Character"
      isSubmitting={isSubmitting}
    >
      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="character_name">Character Name</label>
        <input
          id="character_name"
          name="character_name"
          value={formData.character_name}
          onChange={handleChange}
          style={formStyles.input}
          placeholder="Enter your character's name..."
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="portrait">Portrait Image (optional)</label>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {portraitPreview && (
            <div style={{ position: 'relative' }}>
              <img
                src={portraitPreview}
                alt="Portrait preview"
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '2px solid var(--accent-teal)'
                }}
              />
              <button
                type="button"
                onClick={handleRemovePortrait}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#c0392b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Remove portrait"
              >
                ×
              </button>
            </div>
          )}
          <div style={{ flex: 1 }}>
            <input
              type="file"
              id="portrait"
              name="portrait"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={handlePortraitChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'var(--accent-deep)',
                border: '1px solid var(--accent-teal)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                cursor: 'pointer'
              }}
            />
            <div style={formStyles.hint}>
              Upload a portrait image (JPEG, PNG, WebP, or GIF, max 5MB)
            </div>
          </div>
        </div>
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="appearance">Appearance</label>
        <textarea
          id="appearance"
          name="appearance"
          value={formData.appearance}
          onChange={handleChange}
          style={formStyles.textarea}
          placeholder="Describe your character's physical appearance..."
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="personality">Personality</label>
        <textarea
          id="personality"
          name="personality"
          value={formData.personality}
          onChange={handleChange}
          style={formStyles.textarea}
          placeholder="Describe your character's personality, traits, and mannerisms..."
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="backstory">Backstory</label>
        <textarea
          id="backstory"
          name="backstory"
          value={formData.backstory}
          onChange={handleChange}
          style={formStyles.textarea}
          placeholder="Tell your character's story, history, and motivations..."
        />
      </div>
    </FormModal>
  )
}

export default CharacterForm
